import React, { useState, Fragment } from 'react'
import styled from 'styled-components'

const Section = styled.div`
  margin-top: 2rem;
`

const Title = styled.h2`
  font-size: 1.5rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
`

const KeysContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const KeyCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
`

const KeyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const KeyName = styled.h3`
  font-size: 1.1rem;
  color: var(--color-text);
`

const KeyValue = styled.div`
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  background: rgba(0, 0, 0, 0.3);
  padding: 0.5rem;
  border-radius: 4px;
  margin: 0.5rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const KeyDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-top: 1rem;
  font-size: 0.9rem;
  color: var(--color-text-secondary);

  div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  span {
    color: var(--color-text);
  }
`

const Button = styled.button`
  background: ${props => props.$danger ? 'rgba(255, 50, 50, 0.2)' : 'var(--color-primary)'};
  color: ${props => props.$danger ? '#ff5252' : 'var(--color-text)'};
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.2s;

  &:hover {
    background: ${props => props.$danger ? 'rgba(255, 50, 50, 0.3)' : 'var(--color-primary-dark)'};
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
  }
`

const CreateKeyForm = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`

const Input = styled.input`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 0.5rem;
  color: var(--color-text);
  font-size: 0.9rem;
  width: 100%;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`

const Select = styled.select`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 0.5rem;
  color: var(--color-text);
  font-size: 0.9rem;
  width: 100%;
  margin-bottom: 1rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  option {
    background: #1a1a1a;
  }
`

const ProjectOption = styled.option`
  padding: 0.5rem;
`

const NoKeysMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--color-text-secondary);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
`

const ConnectMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--color-text-secondary);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`

export default function APIKeysSection({ isAuthorized, projects = [] }) {
  const [keys, setKeys] = useState([
    {
      id: 'key_1',
      name: 'Production API Key',
      key: 'sk_live_xyz789...',
      project: 'Website Development',
      created: '2024-01-20T10:30:00Z',
      lastUsed: '2024-01-20T15:45:00Z'
    }
  ])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newKey, setNewKey] = useState({
    name: '',
    project: ''
  })

  const handleCreateKey = () => {
    if (newKey.name && newKey.project) {
      const selectedProject = projects.find(p => p.id === newKey.project)
      const key = {
        id: `key_${Date.now()}`, // Use timestamp for unique ID
        name: newKey.name,
        key: `sk_live_${Math.random().toString(36).substr(2, 9)}`,
        project: selectedProject?.name,
        created: new Date().toISOString(),
        lastUsed: null
      }
      setKeys([...keys, key])
      setNewKey({ name: '', project: '' })
      setShowCreateForm(false)
    }
  }

  const handleRevokeKey = (keyId) => {
    setKeys(keys.filter(k => k.id !== keyId))
  }

  if (!isAuthorized) {
    return (
      <ConnectMessage>
        <div key="connect-message">Connect your wallet or enable Demo Mode to manage API keys</div>
      </ConnectMessage>
    )
  }

  return (
    <Section>
      <Title>API Keys</Title>

      <Button 
        key="create-new-key-button"
        onClick={() => setShowCreateForm(!showCreateForm)}
        style={{ marginBottom: '1rem' }}
      >
        {showCreateForm ? 'Cancel' : 'Create New API Key'}
      </Button>

      {showCreateForm && (
        <CreateKeyForm key="create-key-form">
          <Input
            key="key-name-input"
            type="text"
            placeholder="Key Name"
            value={newKey.name}
            onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
          />
          <Select
            key="project-select"
            value={newKey.project}
            onChange={(e) => setNewKey({ ...newKey, project: e.target.value })}
          >
            <option key="default" value="">Select Project</option>
            {projects.map(project => (
              <ProjectOption key={project.id} value={project.id}>
                {project.name}
              </ProjectOption>
            ))}
          </Select>
          <Button 
            key="generate-key-button"
            onClick={handleCreateKey}
            disabled={!newKey.name || !newKey.project}
          >
            Generate Key
          </Button>
        </CreateKeyForm>
      )}

      <KeysContainer>
        {keys.length > 0 ? (
          keys.map(key => (
            <KeyCard key={key.id}>
              <KeyHeader>
                <KeyName key={`${key.id}-name`}>{key.name}</KeyName>
                <Button 
                  key={`${key.id}-revoke`}
                  $danger
                  onClick={() => handleRevokeKey(key.id)}
                >
                  Revoke
                </Button>
              </KeyHeader>
              <KeyValue key={`${key.id}-value-container`}>
                <span key={`${key.id}-value`}>{key.key}</span>
                <Button
                  key={`${key.id}-copy`}
                  onClick={() => navigator.clipboard.writeText(key.key)}
                  style={{ padding: '0.25rem 0.5rem' }}
                >
                  Copy
                </Button>
              </KeyValue>
              <KeyDetails key={`${key.id}-details`}>
                <Fragment key={`${key.id}-project-group`}>
                  <div key={`${key.id}-project-container`}>
                    <Fragment key={`${key.id}-project-content`}>
                      <span key={`${key.id}-project-label`}>Project:</span>
                      {' '}
                      <span key={`${key.id}-project-value`}>{key.project}</span>
                    </Fragment>
                  </div>
                </Fragment>
                <Fragment key={`${key.id}-created-group`}>
                  <div key={`${key.id}-created-container`}>
                    <Fragment key={`${key.id}-created-content`}>
                      <span key={`${key.id}-created-label`}>Created:</span>
                      {' '}
                      <span key={`${key.id}-created-value`}>{new Date(key.created).toLocaleDateString()}</span>
                    </Fragment>
                  </div>
                </Fragment>
                {key.lastUsed && (
                  <Fragment key={`${key.id}-lastUsed-group`}>
                    <div key={`${key.id}-lastUsed-container`}>
                      <Fragment key={`${key.id}-lastUsed-content`}>
                        <span key={`${key.id}-lastUsed-label`}>Last Used:</span>
                        {' '}
                        <span key={`${key.id}-lastUsed-value`}>{new Date(key.lastUsed).toLocaleDateString()}</span>
                      </Fragment>
                    </div>
                  </Fragment>
                )}
              </KeyDetails>
            </KeyCard>
          ))
        ) : (
          <NoKeysMessage key="no-keys">
            No API keys yet. Create one to get started.
          </NoKeysMessage>
        )}
      </KeysContainer>
    </Section>
  )
}
